/**
 * portal.ts — Subdomain-aware portal detection for VGV.
 *
 * Production subdomains (valuegiftvouchers.com):
 *   app.valuegiftvouchers.com      → consumer
 *   merchant.valuegiftvouchers.com → merchant
 *   corporate.valuegiftvouchers.com → corporate
 *   agent.valuegiftvouchers.com    → agent
 *   admin.valuegiftvouchers.com    → admin
 *
 * Local dev — set VITE_PORTAL in .env.local to simulate a portal:
 *   VITE_PORTAL=consumer | merchant | corporate | agent | admin
 */

export type PortalId = "consumer" | "merchant" | "corporate" | "agent" | "admin";

// --- Subdomain → PortalId ---
const SUBDOMAIN_MAP: Record<string, PortalId> = {
  app:       "consumer",
  merchant:  "merchant",
  corporate: "corporate",
  agent:     "agent",
  admin:     "admin",
};

// --- PortalId → readable label ---
export const PORTAL_LABELS: Record<PortalId, string> = {
  consumer:  "Consumer Wallet",
  merchant:  "Merchant Portal",
  corporate: "Corporate Portal",
  agent:     "Agent Portal",
  admin:     "Admin Console",
};

// --- PortalId → subtitle shown on the login page ---
export const PORTAL_DESCRIPTIONS: Record<PortalId, string> = {
  consumer:  "Access your voucher wallet, check balances, and find nearby merchants.",
  merchant:  "Manage redemptions, view your settlements, and track business performance.",
  corporate: "Issue and manage vouchers for your employees and programmes.",
  agent:     "Activate vouchers, manage your inventory, and earn commissions.",
  admin:     "Platform operator tools and system administration.",
};

// --- PortalId → roles that are allowed to log in here ---
export const PORTAL_ROLES: Record<PortalId, string[]> = {
  consumer:  ["consumer"],
  merchant:  ["merchant_admin", "merchant_staff"],
  corporate: ["corporate_admin", "corporate_finance", "corporate_hr", "corporate_viewer", "corporate_user"],
  agent:     ["agent"],
  admin:     ["super_admin", "compliance"],
};

// --- Role → which portal it belongs to (reverse lookup) ---
export function getPortalForRole(role: string): PortalId | null {
  for (const [portalId, roles] of Object.entries(PORTAL_ROLES)) {
    if (roles.includes(role)) return portalId as PortalId;
  }
  return null;
}

// --- PortalId → subdomain label ---
const PORTAL_SUBDOMAIN: Record<PortalId, string> = {
  consumer:  "app",
  merchant:  "merchant",
  corporate: "corporate",
  agent:     "agent",
  admin:     "admin",
};

const PRODUCTION_DOMAIN = "valuegiftvouchers.com";

/**
 * Detect which portal the app is running in.
 * Returns null on the main domain (no portal context).
 */
export function detectPortal(): PortalId | null {
  // 1. Dev override via env var
  const envPortal = import.meta.env.VITE_PORTAL as string | undefined;
  if (envPortal && envPortal in PORTAL_LABELS) return envPortal as PortalId;

  // 2. Subdomain detection from hostname
  const hostname = window.location.hostname; // e.g. "merchant.valuegiftvouchers.com"
  const parts = hostname.split(".");

  // Handles: subdomain.domain.tld  OR  subdomain.localhost
  if (parts.length >= 2) {
    const subdomain = parts[0];
    if (subdomain in SUBDOMAIN_MAP) return SUBDOMAIN_MAP[subdomain];
  }

  return null;
}

/**
 * Build a full URL for a given portal.
 * - In production: returns https://subdomain.valuegiftvouchers.com{path}
 * - In dev without VITE_PORTAL: returns just {path} (same origin)
 */
export function getPortalUrl(portalId: PortalId, path = "/login"): string {
  const subdomain = PORTAL_SUBDOMAIN[portalId];
  const hostname = window.location.hostname;

  // Production or localhost subdomains
  if (hostname.includes(PRODUCTION_DOMAIN)) {
    return `${window.location.protocol}//${subdomain}.${PRODUCTION_DOMAIN}${path}`;
  }

  // Localhost with real subdomain entries (optional hosts-file setup)
  if (hostname.endsWith(".localhost")) {
    return `${window.location.protocol}//${subdomain}.localhost${path}`;
  }

  // Dev fallback (no subdomains configured) — navigate in-place
  return path;
}
